import { Vector } from '@ardoq/graph';
import { BlocksViewNode, LayoutData } from '../types';
import { last, sortedIndexOf } from 'lodash';
import { isOdd } from '../misc/math';
import { ROOT_NODE_ID } from 'tabview/relationshipDiagrams/consts';

export const getNodeLayoutData = (node: BlocksViewNode) => {
  return {
    col: node.col,
    colSpan: node.colSpan,
    row: node.row,
    rowSpan: node.rowSpan,
    open: node.open,
  };
};

export const buildOrdinates = <Type>(
  nodes: Iterable<Type>,
  minEdge: (node: Type) => number,
  maxEdge: (node: Type) => number
): number[] => {
  const epsilon = 0.01;

  /* initialise event queue and set of ordinates */

  const events = [];
  const ordinates: number[] = [];

  for (const node of nodes) {
    events.push({ isEnter: true, position: minEdge(node) });
    events.push({ isEnter: false, position: maxEdge(node) });
  }

  // lexical sort by event position and edge (exit is before enter)
  // this sort is backwards to that I can conveniently process events
  // from the tail of the event queue

  events.sort((a, b) => {
    if (a.position !== b.position) {
      return b.position - a.position;
    }

    return !a.isEnter && b.isEnter ? 1 : a.isEnter && !b.isEnter ? -1 : 0;
  });

  /* process event queue, adding missing grid lines to ordinate set */

  while (last(events)) {
    const { isEnter, position } = last(events)!;

    if (isEnter && !isOdd(ordinates.length)) {
      /* insert the missing exit before this enter */

      ordinates.push(position - epsilon);
    }

    if (!isEnter && isOdd(ordinates.length)) {
      /* insert the missing enter before this exit */

      ordinates.push(position - epsilon);
    }

    /* insert this position */

    ordinates.push(position);

    /* remove this, and any equivalent, events */

    while (
      last(events) &&
      last(events)?.position === position &&
      last(events)?.isEnter === isEnter
    ) {
      events.pop();
    }
  }

  return ordinates;
};

export const stripGrid = (
  group: BlocksViewNode,
  setLayout: (
    u: BlocksViewNode,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ) => void
) => {
  if (!group.children) {
    return;
  }

  const epsilon = 0.1;
  const leftEdge = (u: BlocksViewNode) => u.col;
  const topEdge = (u: BlocksViewNode) => u.row;
  const rightEdge = (u: BlocksViewNode) => u.col + u.colSpan - epsilon;
  const bottomEdge = (u: BlocksViewNode) => u.row + u.rowSpan - epsilon;

  /* get unique edge ordinates; a linked axis counts its partners' children too */

  const partnersOn = (axis: 'col' | 'row') =>
    (group.alignedWith?.[axis] ?? []).flatMap(
      partner => partner.children ?? []
    );
  const colPartners = partnersOn('col');
  const rowPartners = partnersOn('row');
  const cols = buildOrdinates(
    [...group.children, ...colPartners],
    leftEdge,
    rightEdge
  );
  const rows = buildOrdinates(
    [...group.children, ...rowPartners],
    topEdge,
    bottomEdge
  );

  const restrip = (
    child: BlocksViewNode,
    renumber: { cols: boolean; rows: boolean }
  ) => {
    /* indices within the unique ordinates are the new col and row */

    const col = renumber.cols
      ? sortedIndexOf(cols, leftEdge(child))
      : child.col;
    const row = renumber.rows ? sortedIndexOf(rows, topEdge(child)) : child.row;
    const colSpan = renumber.cols
      ? sortedIndexOf(cols, rightEdge(child)) - col
      : child.colSpan;
    const rowSpan = renumber.rows
      ? sortedIndexOf(rows, bottomEdge(child)) - row
      : child.rowSpan;

    if (
      col !== child.col ||
      row !== child.row ||
      colSpan !== child.colSpan ||
      rowSpan !== child.rowSpan
    ) {
      setLayout(child, col, row, colSpan, rowSpan);
    }
  };

  group.children.forEach(child => restrip(child, { cols: true, rows: true }));
  colPartners.forEach(child => restrip(child, { cols: true, rows: false }));
  rowPartners.forEach(child => restrip(child, { cols: false, rows: true }));
};

export const getColPositions = (group: BlocksViewNode) => {
  const ret: number[] = [];

  ret.push(group.content![0]);
  group.colSize!.forEach(size => ret.push(ret[ret.length - 1] + size));

  return ret;
};

export const getRowPositions = (group: BlocksViewNode) => {
  const ret: number[] = [];

  ret.push(group.content![1]);
  group.rowSize!.forEach(size => ret.push(ret[ret.length - 1] + size));

  return ret;
};

export const getGridPosition = (
  gridNode: BlocksViewNode,
  index: Vector
): Vector => {
  const ret: Vector = [gridNode.content![0], gridNode.content![1]];
  const colSize = gridNode.colSize!;
  const rowSize = gridNode.rowSize!;

  for (let i = 0; i < index[0] && i < colSize.length; ++i) {
    ret[0] += colSize[i];
  }

  for (let i = 0; i < index[1] && i < rowSize.length; ++i) {
    ret[1] += rowSize[i];
  }

  return ret;
};

export const getNodeLayoutIndex = (node: BlocksViewNode) => {
  const nodeIndex = node.id;
  const parent = node.parent;

  const parentIdPrefix =
    !parent || parent.id === ROOT_NODE_ID ? '' : `${parent.id}-`;

  return `${parentIdPrefix}${nodeIndex}`;
};

const addTreeLayoutData = (
  layout: LayoutData,
  node: BlocksViewNode
): LayoutData => {
  layout[getNodeLayoutIndex(node)] = getNodeLayoutData(node);

  return node.children?.reduce(addTreeLayoutData, layout) || layout;
};
/**
 * We avoid saving the root node in the layout data, as it is not really part of the layout.
 */
export const getLayoutData = (nodes: BlocksViewNode[] | null): LayoutData =>
  nodes?.length ? nodes.reduce(addTreeLayoutData, {}) : {};

export const isLayoutSet = (node: BlocksViewNode) =>
  !isNaN(node.col) &&
  !isNaN(node.colSpan) &&
  !isNaN(node.row) &&
  !isNaN(node.rowSpan);
